import React, { useState } from "react";
import {
  Box,
  Divider,
  Text,
  useMediaQuery,
  useTheme,
} from "@origin-digital/ods-core";
import {
  IconDescription,
  IconChevronDownSmall,
} from "@origin-digital/ods-icons";
import { AISourcesSkeleton } from "./AISourcesSkeleton";
import { content } from "./AIAnswer.content";
import {
  FixedHeightWrapper,
  ScrollableSourcesContainer,
  StyledTextLink,
  SummaryWrapper,
  TransparentButton,
} from "./AISources.styles";

const extractSummaryContent = (text: string): string => {
  const parts = text
    .split("\n\n")
    .map((p) => p.trim())
    .filter(Boolean);
  return parts[1] ?? parts[0] ?? "";
};

export interface AiSource {
  url: string;
  title: string;
  summary?: string;
}

export interface AISourcesProps {
  sources: AiSource[];
  answer: string;
  status?: "idle" | "connecting" | "streaming" | "done" | "error";
}

export const AISources: React.FC<AISourcesProps> = ({
  sources,
  status,
  answer,
}) => {
  const [visibleCount, setVisibleCount] = useState(2);
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isLoading =
    status === "connecting" || (status === "streaming" && !answer);

  const handleViewMore = () => {
    if (isMobile) {
      if (visibleCount === 2) {
        setVisibleCount(4);
      } else {
        setVisibleCount(sources.length);
      }
    } else {
      // Desktop: show all in scrollable container
      setVisibleCount(sources.length);
      setIsExpanded(true);
    }
  };

  const visibleSources = sources.slice(0, visibleCount);
  const showViewMore = visibleCount < sources.length;

  if (isLoading) {
    return <AISourcesSkeleton />;
  }

  if (!sources.length) return null;

  return (
    <FixedHeightWrapper $isExpanded={isExpanded && !isMobile}>
      <Box
        outline
        data-id="ai-sources"
        borderRadius="medium"
        width="full"
        display="flex"
        flexDirection="column"
        style={isExpanded && !isMobile ? { flex: 1, minHeight: 0 } : undefined}
      >
        <Box marginBottom="small" padding="large" paddingBottom="none">
          <Text weight="medium" variant="subtitle-small" tone="neutralDark">
            {sources.length}{" "}
            {sources.length === 1
              ? content.sourceSingular
              : content.sourcePlural}
          </Text>
        </Box>

        <ScrollableSourcesContainer $isExpanded={isExpanded && !isMobile}>
          {visibleSources.map((source, index) => {
            const titleSource = source.title.split("|");
            const titleLabel = titleSource[0].trim();
            const tag = titleSource[1]?.trim();

            return (
              <React.Fragment key={source.url}>
                <Box
                  display="flex"
                  flexDirection="row"
                  gap="small"
                  paddingY="large"
                  data-id={`ai-source-row-${index}`}
                  paddingX="large"
                >
                  <Box paddingTop="xxxsmall">
                    <IconDescription
                      size="xxsmall"
                      color="neutralLight"
                      isDecorative
                    />
                  </Box>
                  <Box display="flex" flexDirection="column" gap="xsmall">
                    <Text
                      variant="body-small"
                      tone="neutralLight"
                      weight="medium"
                    >
                      {tag}
                    </Text>
                    <Text variant="body" weight="medium" tone="neutralDark">
                      <StyledTextLink
                        href={source.url}
                        data-id={`ai-source-title-${index}`}
                        target="_blank"
                        domProps={{ rel: "noopener noreferrer" }}
                      >
                        {titleLabel}
                      </StyledTextLink>
                    </Text>
                    <SummaryWrapper>
                      <Text
                        variant="body-small"
                        weight="regular"
                        tone="neutral"
                      >
                        {source.summary
                          ? extractSummaryContent(source.summary)
                          : ""}
                      </Text>
                    </SummaryWrapper>
                  </Box>
                </Box>
                <Box paddingX="large">
                  {index < visibleSources.length - 1 && <Divider />}
                </Box>
              </React.Fragment>
            );
          })}
        </ScrollableSourcesContainer>

        {showViewMore && (
          <Box padding="large">
            <TransparentButton
              variant="outlined"
              color="secondaryB"
              onClick={handleViewMore}
              data-id="ai-sources-view-more-button"
              icon={
                <IconChevronDownSmall
                  size="xxsmall"
                  color="neutralLight"
                  variant="solid"
                />
              }
              iconPosition="trailing"
              fullWidth
              aria-label={content.viewMoreAriaLabel}
            >
              {content.viewMoreLabel}
            </TransparentButton>
          </Box>
        )}
      </Box>
    </FixedHeightWrapper>
  );
};
